import { constructMediaUrl } from './helpers';
import { MediaItem } from './structs/media';

/**
 * URL construction for a media item's bytes, cache-busted by content version.
 *
 * Regenerate/animate/lip-sync rewrite an item in place: same media id, same
 * `file_url`, same `/media/thumb/<id>.webp` — and `/media/uploads/*` is served
 * `public, max-age=3600`. Nothing in the URL changes, so a browser (and a
 * mounted `<video>` whose `src` string is identical) keeps replaying the
 * previous generation. `metadata.content_version` is stamped by the backend
 * whenever fresh bytes are finalised (`CompleteMediaPersistence`,
 * internal/models/viewModel.go), and appending it is what makes the new
 * generation a different URL.
 *
 * These live here rather than beside one consumer because BOTH surfaces that
 * play a media item need them: the storyboard scene panel
 * (`clips/sections/MediaEditorSection.helpers.ts`, which re-exports them) and
 * the clip editor (`clip-editor/editorStore.ts` and friends).
 */

/**
 * The version of the BYTES behind a media item's metadata bag.
 *
 * Absent (e.g. an item generated before this existed) → `undefined`, so callers
 * emit no query param and URLs stay exactly as they were.
 */
export const readContentVersion = (
  metadata: Record<string, any> | undefined
): number | undefined => {
  const raw = metadata?.content_version;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

/** `readContentVersion` for a whole media item. */
export const getMediaContentVersion = (item: MediaItem | undefined): number | undefined =>
  readContentVersion(item?.metadata);

/** `constructMediaUrl` for an item's own file, cache-busted by its content version. */
export const getMediaFileUrl = (item: MediaItem | undefined): string =>
  item?.file_url ? constructMediaUrl(item.file_url, getMediaContentVersion(item)) : '';

/**
 * The backend serves a small webp derivative at `/media/thumb/<media_id>.webp`
 * (see contentgen-go-backend media/serveThumbnail.go). It is the same URL the
 * media library grid consumes, and it may 404 when no derivative has been warmed
 * — so callers MUST fall back to the original `file_url` via onError. Returns ''
 * for a missing id.
 *
 * Takes id + version rather than the item so callers holding only an id (the
 * editor's `EditorMedia`) reach the same URL the item-shaped callers do.
 */
export const mediaThumbUrl = (mediaId: string, contentVersion?: number): string =>
  mediaId ? constructMediaUrl(`/media/thumb/${mediaId}.webp`, contentVersion) : '';

/** `mediaThumbUrl` for a whole media item. */
export const getMediaThumbUrl = (item: MediaItem | undefined): string =>
  mediaThumbUrl(item?.id || '', getMediaContentVersion(item));
